import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { api } from '../lib/api';
import { apiEspace } from '../lib/espace';
import { etatInvitation, typeErreurInvitation, type TypeErreurInvitation } from '../lib/invitation-etat';
import { useAuthStore } from '../store/auth.store';

interface Invitation {
  email: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  /** Profil commun de la suite (04/10/2026). */
  profil?: 'STANDARD' | 'LECTURE_SEULE';
  espace: string;
  expiresAt: string;
}

const ROLES: Record<Invitation['role'], string> = {
  OWNER: 'propriétaire',
  ADMIN: 'administrateur',
  MEMBER: 'membre',
};

/**
 * Invitation à rejoindre un compte.
 *
 * UN seul écran pour les deux situations, et c'est ce qui le rend fiable :
 * l'invitation est d'abord AFFICHÉE — qui invite, à quoi, avec quel rôle —
 * puis, selon qu'on a une session ou non, on accepte ou on est renvoyé vers
 * l'inscription en emportant le jeton.
 *
 * Deux liens différents dans l'e-mail auraient voulu dire deviner à l'envoi si
 * l'adresse a déjà un compte SEmply. On se serait trompé une fois sur deux pour
 * les gens qui en ont un sous une autre adresse.
 *
 * L'invitation est montrée AVANT d'être acceptée, jamais consommée à
 * l'ouverture du lien : un aperçu dans une messagerie, un antivirus qui suit
 * les liens, et le compte serait rejoint sans que personne l'ait décidé.
 */
export function InvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, loading: sessionLoading, fetchMe } = useAuthStore();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [erreurInvitation, setErreurInvitation] = useState<TypeErreurInvitation | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [rejoint, setRejoint] = useState(false);

  // La session doit être DEMANDÉE : arrivé par le lien de l'e-mail, aucun autre
  // écran ne l'a fait, et `loading` restait vrai à jamais (« Vérification de
  // votre session… » sans fin, 05/10/2026). `fetchMe` a désormais un délai.
  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  const lireInvitation = useCallback(() => {
    if (!token) return;
    setErreurInvitation(null);
    api
      .get<Invitation>(`/invitations?token=${encodeURIComponent(token)}`)
      .then(setInvitation)
      .catch((e) => setErreurInvitation(typeErreurInvitation(e)));
  }, [token]);

  useEffect(() => {
    lireInvitation();
  }, [lireInvitation]);

  const accepter = async () => {
    if (!token) return;
    setEnCours(true);
    setErreur(null);
    try {
      // Route de SESSION (`/espace/…`, cookie du portail), et non
      // `/accounts/…` qui exige un access token que le portail n'a pas : 401
      // systématique jusqu'au 04/10/2026 (ZO-AUTH-01).
      await apiEspace.accepterInvitation(token);
      setRejoint(true);
    } catch (e) {
      setErreur(
        (e as { message?: string })?.message ??
          'L’invitation n’a pas pu être acceptée.',
      );
    } finally {
      setEnCours(false);
    }
  };

  const etat = etatInvitation({
    token,
    invitation,
    erreurInvitation,
    sessionPrete: !sessionLoading,
    user,
    rejoint,
  });

  if (etat === 'lien-incomplet') {
    return (
      <AuthCard title="Invitation">
        <Alert message="Lien incomplet : le jeton d’invitation est absent ou illisible. Demandez à la personne qui vous a invité de vous la renvoyer." />
        <Link className="btn btn-secondary" to="/login">
          Aller à la connexion
        </Link>
      </AuthCard>
    );
  }

  if (etat === 'invitation-invalide') {
    return (
      <AuthCard title="Invitation">
        <Alert message="Cette invitation est introuvable, déjà utilisée ou expirée. Demandez à la personne qui vous a invité de vous en envoyer une nouvelle." />
        <Link className="btn btn-secondary" to="/login">
          Aller à la connexion
        </Link>
      </AuthCard>
    );
  }

  if (etat === 'erreur-reseau') {
    return (
      <AuthCard title="Invitation">
        <Alert message="Impossible de joindre le serveur pour lire l’invitation. Vérifiez votre connexion, puis réessayez." />
        <button className="btn btn-primary" type="button" onClick={lireInvitation}>
          Réessayer
        </button>
      </AuthCard>
    );
  }

  if (!invitation) {
    return (
      <AuthCard title="Invitation">
        <p className="subtitle">Chargement…</p>
      </AuthCard>
    );
  }

  if (rejoint) {
    return (
      <AuthCard title="Invitation acceptée">
        <Alert
          message={`Vous faites désormais partie de ${invitation.espace}.`}
          variant="success"
        />
        <button className="btn btn-primary" type="button" onClick={() => navigate('/produits')}>
          Voir mes produits
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={`Rejoindre ${invitation.espace}`}>
      <p className="subtitle">
        Vous êtes invité à rejoindre <strong>{invitation.espace}</strong> en tant
        que <strong>{ROLES[invitation.role]}</strong>
        {invitation.profil === 'LECTURE_SEULE' ? (
          <>
            , en <strong>lecture seule</strong> : vous consulterez ses dossiers sans
            pouvoir rien modifier
          </>
        ) : null}
        .
      </p>
      <p className="subtitle">
        Vous avez déjà un compte SEmply ? Vous le gardez, avec votre propre espace :
        ce compte s’ajoute simplement à ceux auxquels vous avez accès.
      </p>
      <p className="subtitle">
        Rejoindre un compte donne accès à ses dossiers dans les produits SEmply,
        selon le rôle qui vous est confié. Vos données personnelles, elles, ne
        sont pas partagées.
      </p>

      {erreur ? <Alert message={erreur} /> : null}

      {etat === 'verification-session' ? (
        <p className="subtitle">Vérification de votre session…</p>
      ) : user ? (
        <>
          {etat === 'mauvais-compte' ? (
            <Alert
              message={`Cette invitation a été émise pour ${invitation.email}, et vous êtes connecté avec ${user.email}. Déconnectez-vous, puis rouvrez ce lien.`}
            />
          ) : (
            <button
              className="btn btn-primary"
              type="button"
              disabled={enCours}
              onClick={() => void accepter()}
            >
              {enCours ? 'Un instant…' : 'Rejoindre le compte'}
            </button>
          )}
        </>
      ) : (
        <>
          {/* Le jeton VOYAGE avec la navigation : l'inscription le renvoie au
              serveur, qui fait alors rejoindre cet espace au lieu d'en créer un
              nouveau. Sans lui, la personne repartirait avec un compte
              solitaire et ne comprendrait pas pourquoi elle ne voit rien. */}
          <button
            className="btn btn-primary"
            type="button"
            onClick={() =>
              navigate(
                `/register?invitation=${encodeURIComponent(token ?? '')}&email=${encodeURIComponent(invitation.email)}`,
              )
            }
          >
            Créer mon compte
          </button>
          {/* `suite` et non `next` (04/10/2026) : la page de connexion
              n'a jamais lu `next` — après connexion, on partait vers les
              produits et l'invitation était perdue. `suite` est vérifié
              contre une liste fermée (cf. lib/products.ts). */}
          <Link
            className="btn btn-secondary"
            to={`/login?suite=${encodeURIComponent(`/invitation?token=${token ?? ''}`)}`}
          >
            J’ai déjà un compte SEmply
          </Link>
        </>
      )}
    </AuthCard>
  );
}

export default InvitationPage;
